import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus, Search, Trash2, Pencil, Download, ArrowLeft, FileText,
} from "lucide-react";
import toast from "react-hot-toast";
import { backendApi } from "./api/backendApi";

// ──────────────────────────────────────────────────────────────
//  Company profile shown on every generated invoice. Change these if the
//  billing entity ever changes.
// ──────────────────────────────────────────────────────────────
const COMPANY_LOGO = "https://i.8upload.com/image/cc083bfc71232f00/logofinal.png";
const COMPANY = {
  name: "Webideal",
  tagline: "Enterprise Software Solutions",
  address: "Hyderabad, India",
  phone: "+91 98765 43210",
  website: "https://webidealglobal.com/",
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Service catalogue offered to clients. Selecting one snapshots its
// description onto the invoice line item at save time, so historical
// invoices stay accurate even if this catalogue changes later.
const SERVICE_PRESETS = {
  crm_hr: {
    service: "CRM & HRMS Platform",
    subtitle: "Monthly Licence",
    title: "Cloud SaaS, CRM & HR Management System",
    bullets: ["Client Management • Attendance • Payroll", "Leave Management • Reporting"],
    plan: "Business",
  },
  hrms: {
    service: "HRMS Platform",
    subtitle: "Monthly Licence",
    title: "Cloud SaaS, Human Resource Management System",
    bullets: ["Attendance • Payroll • Leave Management", "Onboarding • Compliance • Reporting"],
    plan: "Business",
  },
  ats: {
    service: "Applicant Tracking System",
    subtitle: "Monthly Licence",
    title: "Cloud SaaS, Applicant Tracking System",
    bullets: ["AI-Powered Screening • Candidate Ranking", "Automated Workflow Management • Interview Scheduling • Reporting"],
    plan: "Business",
  },
};
const SERVICE_KEYS = Object.keys(SERVICE_PRESETS);

const STATUS_OPTIONS = ["DRAFT", "ACTIVE", "PAID", "OVERDUE", "CANCELLED"];
const STATUS_COLORS = {
  DRAFT: "#64748B",
  ACTIVE: "#2E7D46",
  PAID: "#1E73A8",
  OVERDUE: "#B23B3B",
  CANCELLED: "#7C3AED",
};
const statusBadgeCls = (status) => {
  const map = {
    DRAFT: "bg-slate-100 text-slate-600",
    ACTIVE: "bg-green-100 text-green-700",
    PAID: "bg-blue-100 text-blue-700",
    OVERDUE: "bg-red-100 text-red-700",
    CANCELLED: "bg-purple-100 text-purple-700",
  };
  return map[status] || "bg-slate-100 text-slate-600";
};

// ---------- Helpers ----------
const pad2 = (n) => String(n).padStart(2, "0");
const daysInMonth = (monthIndex, year) => new Date(year, monthIndex + 1, 0).getDate();

// Accepts either "YYYY-MM-DD" or a full ISO datetime (what the DATE columns
// round-trip as once JSON-serialized) and always renders the calendar date,
// never shifted by the browser's local timezone.
const isoDateOnly = (value) => (value ? String(value).slice(0, 10) : "");
const formatDateDMY = (isoDate) => {
  const only = isoDateOnly(isoDate);
  if (!only) return "-";
  const d = new Date(`${only}T00:00:00`);
  return `${pad2(d.getDate())} ${MONTHS[d.getMonth()].slice(0, 3)} ${d.getFullYear()}`;
};
const formatINR = (num) => {
  const n = Number(num) || 0;
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const addDays = (isoDate, days) => {
  const d = new Date(`${isoDate}T00:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

let rowIdCounter = 1;
const newRow = (monthIdx, year) => ({
  id: rowIdCounter++,
  serviceKey: "hrms",
  users: 40,
  rate: 1200,
  month: monthIdx,
  year,
});

// ---------- jsPDF + logo loading (cached module-wide so we only fetch once) ----------
const loadScriptOnce = (src) =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(s);
  });

const fetchAsDataURL = (url) =>
  new Promise((resolve, reject) => {
    fetch(url, { mode: "cors" })
      .then((res) => {
        if (!res.ok) throw new Error(`Bad response: ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Blob read failed"));
        reader.readAsDataURL(blob);
      })
      .catch(reject);
  });

const loadLogoDataURL = async (url) => {
  try {
    return await fetchAsDataURL(url);
  } catch (e) {
    console.warn("Direct logo fetch blocked by CORS, trying proxy:", e);
  }
  try {
    const bare = url.replace(/^https?:\/\//, "");
    const proxied = `https://images.weserv.nl/?url=${encodeURIComponent(bare)}`;
    return await fetchAsDataURL(proxied);
  } catch (e) {
    console.warn("Logo proxy fetch also failed:", e);
  }
  return null;
};

let pdfEnginePromise = null;
// Loads jsPDF (once) plus the real company logo as a data URL (once), and
// caches both so re-opening the editor / downloading another invoice never
// re-fetches them.
const ensurePdfEngine = () => {
  if (!pdfEnginePromise) {
    pdfEnginePromise = loadScriptOnce("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js")
      .then(() => loadLogoDataURL(COMPANY_LOGO))
      .then((logoDataUrl) => ({ logoDataUrl }));
  }
  return pdfEnginePromise;
};

// ──────────────────────────────────────────────────────────────
//  Builds a genuine, selectable-text PDF for a saved invoice record
//  (as returned by backendApi.invoices.get/create/update, i.e. including
//  `items`) and saves it to disk. Mirrors the standalone invoice generator's
//  layout so PDFs look the same as before this was folded into the HRMS.
// ──────────────────────────────────────────────────────────────
async function generateInvoicePdf(record) {
  const { logoDataUrl } = await ensurePdfEngine();
  if (!window.jspdf) throw new Error("PDF engine failed to load");

  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 40;
  const contentW = pageWidth - marginX * 2;

  const blue = [30, 111, 168];
  const navy = [18, 35, 58];
  const gray = [107, 119, 133];
  const dark = [27, 39, 51];
  const border = [218, 227, 236];
  const lightBlueBg = [238, 244, 250];
  const statusColorMap = {
    DRAFT: [100, 116, 139],
    ACTIVE: [46, 125, 70],
    PAID: [30, 115, 168],
    OVERDUE: [178, 59, 59],
    CANCELLED: [124, 58, 237],
  };

  const client = {
    name: record.client_name,
    address: record.client_address || "-",
    contact: record.client_contact || "-",
    email: record.client_email || "-",
    phone: record.client_phone || "-",
  };
  const invoiceNumber = record.invoice_number;
  const status = record.status || "DRAFT";
  const invoiceDateOnly = isoDateOnly(record.invoice_date);
  const dueDateOnly = isoDateOnly(record.due_date);
  const paymentTermsDays = record.payment_terms_days || 14;
  const periodMonth = record.period_month ?? new Date(`${invoiceDateOnly}T00:00:00`).getMonth();
  const periodYear = record.period_year ?? new Date(`${invoiceDateOnly}T00:00:00`).getFullYear();
  const periodDays = daysInMonth(periodMonth, periodYear);
  const servicePeriodText = `01 ${MONTHS[periodMonth].slice(0, 3)} ${periodYear}, ${pad2(periodDays)} ${MONTHS[periodMonth].slice(0, 3)} ${periodYear}`;
  const rows = record.items || [];
  const grandTotal = Number(record.grand_total) || 0;

  let y = 46;

  const logoSize = 36;
  const logoX = marginX;
  const logoY = y - 16;
  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, logoX, logoY, logoSize, logoSize);
    } catch (e) {
      console.warn("Could not embed logo image, using fallback mark:", e);
    }
  } else {
    doc.setFillColor(...blue);
    doc.circle(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2, "F");
  }

  const textX = marginX + logoSize + 12;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(19);
  doc.setTextColor(...dark);
  doc.text(COMPANY.name, textX, y);

  doc.setFontSize(11);
  doc.setTextColor(...blue);
  doc.text(COMPANY.tagline, textX, y + 15);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...gray);
  doc.text(COMPANY.address, textX, y + 28);
  doc.text(`${COMPANY.phone} | ${COMPANY.website}`, textX, y + 39);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(25);
  doc.setTextColor(...blue);
  doc.text("INVOICE", pageWidth - marginX, y, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...gray);
  doc.text(`# ${invoiceNumber}`, pageWidth - marginX, y + 15, { align: "right" });

  const badgeColor = statusColorMap[status] || blue;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  const badgeW = doc.getTextWidth(status) + 18;
  doc.setFillColor(...badgeColor);
  doc.roundedRect(pageWidth - marginX - badgeW, y + 22, badgeW, 16, 2, 2, "F");
  doc.setTextColor(255, 255, 255);
  doc.text(status, pageWidth - marginX - badgeW / 2, y + 33, { align: "center" });

  y += 56;
  doc.setDrawColor(...blue);
  doc.setLineWidth(1.6);
  doc.line(marginX, y, pageWidth - marginX, y);
  y += 20;

  const colGap = 12;
  const colW = (contentW - colGap) / 2;
  const col1X = marginX;
  const col2X = marginX + colW + colGap;
  const boxTop = y;
  const headerH = 20;

  doc.setFillColor(...blue);
  doc.rect(col1X, boxTop, colW, headerH, "F");
  doc.rect(col2X, boxTop, colW, headerH, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(255, 255, 255);
  doc.text("INVOICE DETAILS", col1X + 8, boxTop + 13);
  doc.text("BILL TO", col2X + 8, boxTop + 13);

  const leftRows = [
    ["Invoice No.", invoiceNumber],
    ["Invoice Date", formatDateDMY(invoiceDateOnly)],
    ["Due Date", formatDateDMY(dueDateOnly)],
    ["Payment Terms", `Net ${paymentTermsDays} Days`],
    ["Service Period", servicePeriodText],
  ];
  const rightRows = [
    ["Client", client.name],
    ["Address", client.address],
    ["Contact", client.contact],
    ["Email", client.email],
    ["Phone", client.phone],
  ];

  const rowH = 26;
  const rowsTop = boxTop + headerH;
  const maxInfoRows = Math.max(leftRows.length, rightRows.length);

  for (let i = 0; i < maxInfoRows; i++) {
    const ry = rowsTop + i * rowH;
    if (i % 2 === 0) {
      doc.setFillColor(...lightBlueBg);
      doc.rect(col1X, ry, colW, rowH, "F");
      doc.rect(col2X, ry, colW, rowH, "F");
    }
    doc.setDrawColor(...border);
    doc.setLineWidth(0.6);
    doc.rect(col1X, ry, colW, rowH);
    doc.rect(col2X, ry, colW, rowH);

    if (leftRows[i]) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.2);
      doc.setTextColor(...gray);
      doc.text(leftRows[i][0], col1X + 8, ry + 16);
      doc.setFontSize(8.6);
      doc.setTextColor(...dark);
      doc.text(String(leftRows[i][1]), col1X + colW * 0.34, ry + 16, { maxWidth: colW * 0.62 });
    }
    if (rightRows[i]) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.2);
      doc.setTextColor(...gray);
      doc.text(rightRows[i][0], col2X + 8, ry + 16);
      doc.setFontSize(8.6);
      doc.setTextColor(...dark);
      doc.text(String(rightRows[i][1]), col2X + colW * 0.34, ry + 16, { maxWidth: colW * 0.62 });
    }
  }

  y = rowsTop + maxInfoRows * rowH + 20;

  const colSR = 32, colSERVICE = 85, colUSERS = 45, colRATE = 65, colTOTAL = 75;
  const colDESC = contentW - (colSR + colSERVICE + colUSERS + colRATE + colTOTAL);
  const xSR = marginX;
  const xSERVICE = xSR + colSR;
  const xDESC = xSERVICE + colSERVICE;
  const xUSERS = xDESC + colDESC;
  const xRATE = xUSERS + colUSERS;
  const xTOTAL = xRATE + colRATE;
  const tableRight = marginX + contentW;

  const theadH = 22;
  doc.setFillColor(...navy);
  doc.rect(marginX, y, contentW, theadH, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.text("SR", xSR + 6, y + 14);
  doc.text("SERVICE", xSERVICE + 6, y + 14);
  doc.text("DESCRIPTION", xDESC + 6, y + 14);
  doc.text("USERS", xUSERS + colUSERS - 6, y + 14, { align: "right" });
  doc.text("RATE / USER", xRATE + colRATE - 6, y + 14, { align: "right" });
  doc.text("TOTAL (Rs.)", xTOTAL + colTOTAL - 6, y + 14, { align: "right" });
  y += theadH;

  const padTop = 12;
  const padSide = 8;
  const lineH = 10.5;

  rows.forEach((item, idx) => {
    const itemMonth = item.month ?? periodMonth;
    const itemYear = item.year ?? periodYear;
    const days = daysInMonth(itemMonth, itemYear);
    const total = Number(item.total) || (Number(item.users) || 0) * (Number(item.rate) || 0);
    const bullets = Array.isArray(item.bullets) ? item.bullets : [];

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    const titleLines = doc.splitTextToSize(item.title || item.service_name || "", colDESC - padSide * 2);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    const bulletLines = [];
    bullets.forEach((b) => {
      const wrapped = doc.splitTextToSize(`•  ${b}`, colDESC - padSide * 2);
      bulletLines.push(...wrapped);
    });

    const metaLines = [
      `Billing Cycle: ${item.billing_cycle || "Monthly"}`,
      `Plan: ${item.plan || "-"}`,
      `Period: ${days} Days (${MONTHS[itemMonth]} ${itemYear})`,
    ];

    const contentLineCount = titleLines.length + bulletLines.length + metaLines.length;
    const rowH2 = padTop + contentLineCount * lineH + 10;

    if (y + rowH2 > pageHeight - 90) {
      doc.addPage();
      y = 40;
    }

    if (idx % 2 === 1) {
      doc.setFillColor(250, 252, 254);
      doc.rect(marginX, y, contentW, rowH2, "F");
    }
    doc.setDrawColor(...border);
    doc.setLineWidth(0.6);
    doc.rect(xSR, y, colSR, rowH2);
    doc.rect(xSERVICE, y, colSERVICE, rowH2);
    doc.rect(xDESC, y, colDESC, rowH2);
    doc.rect(xUSERS, y, colUSERS, rowH2);
    doc.rect(xRATE, y, colRATE, rowH2);
    doc.rect(xTOTAL, y, colTOTAL, rowH2);

    const ty = y + padTop;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...blue);
    doc.text(pad2(idx + 1), xSR + 6, ty);

    const serviceLines = doc.splitTextToSize(item.service_name || "", colSERVICE - 12);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.6);
    doc.setTextColor(...dark);
    let serviceY = ty;
    serviceLines.forEach((line) => {
      doc.text(line, xSERVICE + 6, serviceY);
      serviceY += 10;
    });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.8);
    doc.setTextColor(...gray);
    doc.text(item.subtitle || "", xSERVICE + 6, serviceY + 2, { maxWidth: colSERVICE - 12 });

    let dy = ty;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...dark);
    titleLines.forEach((line) => {
      doc.text(line, xDESC + padSide, dy);
      dy += lineH;
    });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...gray);
    bulletLines.forEach((line) => {
      doc.text(line, xDESC + padSide, dy);
      dy += lineH;
    });
    metaLines.forEach((line) => {
      doc.text(line, xDESC + padSide, dy);
      dy += lineH;
    });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.6);
    doc.setTextColor(...dark);
    doc.text(String(item.users), xUSERS + colUSERS - 6, ty, { align: "right" });
    doc.text(`Rs. ${item.rate}`, xRATE + colRATE - 6, ty, { align: "right" });
    doc.text(`Rs. ${formatINR(total)}`, xTOTAL + colTOTAL - 6, ty, { align: "right" });

    y += rowH2;
  });

  doc.setFillColor(...blue);
  doc.rect(marginX, y, contentW, 26, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text("GRAND TOTAL", marginX + 10, y + 17);
  doc.setFontSize(13);
  doc.text(`Rs. ${formatINR(grandTotal)}`, tableRight - 10, y + 17, { align: "right" });
  y += 44;

  doc.setDrawColor(...border);
  doc.setLineWidth(0.6);
  doc.line(marginX, y, tableRight, y);
  y += 16;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...gray);
  doc.text(`${COMPANY.name}  |  ${COMPANY.address}  |  ${COMPANY.website}`, pageWidth / 2, y, { align: "center" });

  doc.setProperties({ title: invoiceNumber });

  const pdfBlob = doc.output("blob");
  if (window.showSaveFilePicker) {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: `${invoiceNumber}.pdf`,
        types: [{ description: "PDF file", accept: { "application/pdf": [".pdf"] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(pdfBlob);
      await writable.close();
      return;
    } catch (pickerErr) {
      if (pickerErr && pickerErr.name === "AbortError") return;
      console.warn("Save picker unavailable/failed, falling back to direct download:", pickerErr);
    }
  }
  doc.save(`${invoiceNumber}.pdf`);
}

const inputCls =
  "w-full h-10 px-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30";
const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

// ──────────────────────────────────────────────────────────────
//  Root component, mounted as the "Invoices" tab of the Superadmin console.
// ──────────────────────────────────────────────────────────────
export default function Invoices() {
  const queryClient = useQueryClient();
  const [view, setView] = useState("list"); // 'list' | 'editor'
  const [editingId, setEditingId] = useState(null); // null = new invoice
  const [loadingRecord, setLoadingRecord] = useState(false);

  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ["sa-invoices"],
    queryFn: () => backendApi.invoices.list(),
  });

  const { data: tenants = [] } = useQuery({
    queryKey: ["sa-tenants-lite"],
    queryFn: () => backendApi.superadmin.listTenants(),
  });

  const openNew = () => {
    setEditingId(null);
    setView("editor");
  };

  const openEdit = async (id) => {
    setLoadingRecord(true);
    try {
      setEditingId(id);
      setView("editor");
    } finally {
      setLoadingRecord(false);
    }
  };

  const backToList = () => {
    setView("list");
    setEditingId(null);
  };

  const onSaved = () => {
    queryClient.invalidateQueries({ queryKey: ["sa-invoices"] });
    backToList();
  };

  if (view === "editor") {
    return (
      <InvoiceEditor
        invoiceId={editingId}
        tenants={tenants}
        onCancel={backToList}
        onSaved={onSaved}
        loadingRecord={loadingRecord}
      />
    );
  }

  return (
    <InvoiceList
      invoices={invoices}
      isLoading={isLoading}
      tenants={tenants}
      onNew={openNew}
      onEdit={openEdit}
      queryClient={queryClient}
    />
  );
}

// ── List view ──
function InvoiceList({ invoices, isLoading, onNew, onEdit, queryClient }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);

  const deleteMutation = useMutation({
    mutationFn: (id) => backendApi.invoices.remove(id),
    onSuccess: () => {
      toast.success("Invoice deleted");
      queryClient.invalidateQueries({ queryKey: ["sa-invoices"] });
    },
    onError: (e) => toast.error(e.message || "Could not delete invoice"),
  });

  const filtered = useMemo(() => {
    return invoices.filter((inv) => {
      if (statusFilter && inv.status !== statusFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        inv.client_name?.toLowerCase().includes(q) ||
        inv.invoice_number?.toLowerCase().includes(q) ||
        inv.tenant_name?.toLowerCase().includes(q)
      );
    });
  }, [invoices, search, statusFilter]);

  const totalOutstanding = useMemo(
    () => invoices.filter((i) => i.status === "ACTIVE" || i.status === "OVERDUE")
      .reduce((s, i) => s + (Number(i.grand_total) || 0), 0),
    [invoices]
  );

  const handleDownload = async (id) => {
    setDownloadingId(id);
    try {
      const record = await backendApi.invoices.get(id);
      await generateInvoicePdf(record);
    } catch (e) {
      toast.error(e.message || "Could not generate PDF");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-800">Invoices</h2>
          <p className="text-slate-500 text-sm">Create and manage invoices for any client</p>
        </div>
        <button
          onClick={onNew}
          className="h-11 px-5 rounded-xl text-sm font-bold inline-flex items-center gap-2 text-white bg-teal-600 hover:bg-teal-700"
        >
          <Plus className="w-4 h-4" /> New Invoice
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="rounded-2xl p-5 bg-white shadow-sm border border-slate-100">
          <p className="text-2xl font-extrabold text-slate-800">{invoices.length}</p>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mt-1">Total Invoices</p>
        </div>
        <div className="rounded-2xl p-5 bg-white shadow-sm border border-slate-100">
          <p className="text-2xl font-extrabold text-amber-600">Rs. {formatINR(totalOutstanding)}</p>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mt-1">Outstanding</p>
        </div>
        <div className="rounded-2xl p-5 bg-white shadow-sm border border-slate-100">
          <p className="text-2xl font-extrabold text-green-600">
            {invoices.filter((i) => i.status === "PAID").length}
          </p>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mt-1">Paid</p>
        </div>
        <div className="rounded-2xl p-5 bg-white shadow-sm border border-slate-100">
          <p className="text-2xl font-extrabold text-red-600">
            {invoices.filter((i) => i.status === "OVERDUE").length}
          </p>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mt-1">Overdue</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search invoice #, client…"
            className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-11 px-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <div className="rounded-2xl bg-white shadow-sm border border-slate-100 overflow-hidden">
        {isLoading ? (
          <p className="text-slate-400 p-6">Loading invoices…</p>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-400">
            <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            No invoices yet. Click "New Invoice" to create one.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="px-4 py-3 font-semibold">Invoice #</th>
                <th className="px-4 py-3 font-semibold">Client</th>
                <th className="px-4 py-3 font-semibold">Period</th>
                <th className="px-4 py-3 font-semibold">Invoice Date</th>
                <th className="px-4 py-3 font-semibold text-right">Total</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((inv) => (
                <tr key={inv.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-slate-800">{inv.invoice_number}</td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-slate-800">{inv.client_name}</p>
                    {inv.tenant_name && <p className="text-xs text-slate-400">{inv.tenant_name}</p>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {inv.period_month != null && inv.period_year
                      ? `${MONTHS[inv.period_month]} ${inv.period_year}`
                      : "N/A"}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{formatDateDMY(inv.invoice_date)}</td>
                  <td className="px-4 py-3 text-right font-bold text-slate-800">Rs. {formatINR(inv.grand_total)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-bold uppercase px-2 py-1 rounded-full ${statusBadgeCls(inv.status)}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => handleDownload(inv.id)}
                      disabled={downloadingId === inv.id}
                      className="text-slate-400 hover:text-teal-600 disabled:opacity-50 mr-3"
                      title="Download PDF"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onEdit(inv.id)}
                      className="text-slate-400 hover:text-slate-700 mr-3"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() =>
                        window.confirm(`Delete invoice ${inv.invoice_number}? This cannot be undone.`) &&
                        deleteMutation.mutate(inv.id)
                      }
                      disabled={deleteMutation.isLoading}
                      className="text-slate-300 hover:text-red-500 disabled:opacity-50"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Editor: create or edit an invoice, with a live preview ──
function InvoiceEditor({ invoiceId, tenants, onCancel, onSaved, loadingRecord }) {
  const isEditing = !!invoiceId;
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [savedNumber, setSavedNumber] = useState(null);

  const now = new Date();
  const [tenantId, setTenantId] = useState("");
  const [client, setClient] = useState({ name: "", address: "", contact: "", email: "", phone: "" });
  const [invoiceDate, setInvoiceDate] = useState(todayISO());
  const [paymentTermsDays, setPaymentTermsDays] = useState(14);
  const [status, setStatus] = useState("DRAFT");
  const [periodMonth, setPeriodMonth] = useState(now.getMonth());
  const [periodYear, setPeriodYear] = useState(now.getFullYear());
  const [rows, setRows] = useState([newRow(now.getMonth(), now.getFullYear())]);
  const [previewNumber, setPreviewNumber] = useState("");

  // New invoice: show the number it *would* get once saved.
  useEffect(() => {
    if (isEditing) return;
    backendApi.invoices.nextNumber(new Date(invoiceDate).getFullYear())
      .then((r) => setPreviewNumber(r.invoice_number))
      .catch(() => setPreviewNumber(""));
  }, [isEditing, invoiceDate]);

  // Editing an existing invoice: load its saved data into the form.
  useEffect(() => {
    if (!isEditing) return;
    let active = true;
    setLoading(true);
    backendApi.invoices.get(invoiceId)
      .then((record) => {
        if (!active) return;
        setTenantId(record.tenant_id || "");
        setClient({
          name: record.client_name || "",
          address: record.client_address || "",
          contact: record.client_contact || "",
          email: record.client_email || "",
          phone: record.client_phone || "",
        });
        setInvoiceDate(isoDateOnly(record.invoice_date) || todayISO());
        setPaymentTermsDays(record.payment_terms_days || 14);
        setStatus(record.status || "DRAFT");
        setPeriodMonth(record.period_month ?? now.getMonth());
        setPeriodYear(record.period_year ?? now.getFullYear());
        setSavedNumber(record.invoice_number);
        setRows(
          (record.items || []).map((it) => ({
            id: rowIdCounter++,
            serviceKey: it.service_key && SERVICE_PRESETS[it.service_key] ? it.service_key : "hrms",
            users: it.users,
            rate: it.rate,
            month: it.month ?? record.period_month ?? now.getMonth(),
            year: it.year ?? record.period_year ?? now.getFullYear(),
          }))
        );
      })
      .catch((e) => toast.error(e.message || "Could not load invoice"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, isEditing]);

  const dueDate = addDays(invoiceDate, Number(paymentTermsDays) || 0);
  const periodDays = daysInMonth(periodMonth, periodYear);
  const servicePeriodText = `01 ${MONTHS[periodMonth].slice(0, 3)} ${periodYear}, ${pad2(periodDays)} ${MONTHS[periodMonth].slice(0, 3)} ${periodYear}`;
  const grandTotal = rows.reduce((sum, r) => sum + (Number(r.users) || 0) * (Number(r.rate) || 0), 0);
  const invoiceNumber = savedNumber || previewNumber || "N/A";

  const updateRow = (id, patch) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const addRow = () => setRows((prev) => [...prev, newRow(periodMonth, periodYear)]);
  const removeRow = (id) => setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));

  const onSelectTenant = (e) => {
    const id = e.target.value;
    setTenantId(id);
    const t = tenants.find((x) => String(x.id) === String(id));
    if (t) setClient((c) => ({ ...c, name: c.name ? c.name : t.name }));
  };

  const buildPayload = () => ({
    tenant_id: tenantId || null,
    client_name: client.name,
    client_address: client.address,
    client_contact: client.contact,
    client_email: client.email,
    client_phone: client.phone,
    invoice_date: invoiceDate,
    due_date: dueDate,
    payment_terms_days: Number(paymentTermsDays) || 14,
    period_month: periodMonth,
    period_year: periodYear,
    status,
    items: rows.map((r) => {
      const preset = SERVICE_PRESETS[r.serviceKey];
      return {
        service_key: r.serviceKey,
        service_name: preset.service,
        subtitle: preset.subtitle,
        title: preset.title,
        bullets: preset.bullets,
        plan: preset.plan,
        billing_cycle: "Monthly",
        users: Number(r.users) || 0,
        rate: Number(r.rate) || 0,
        month: r.month,
        year: r.year,
      };
    }),
  });

  const validate = () => {
    if (!client.name.trim()) {
      toast.error("Client name is required");
      return false;
    }
    if (rows.length === 0) {
      toast.error("Add at least one service line item");
      return false;
    }
    return true;
  };

  const persist = async () => {
    if (!validate()) return null;
    setSaving(true);
    try {
      const payload = buildPayload();
      const record = isEditing
        ? await backendApi.invoices.update(invoiceId, payload)
        : await backendApi.invoices.create(payload);
      setSavedNumber(record.invoice_number);
      return record;
    } catch (e) {
      toast.error(e.message || "Could not save invoice");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const record = await persist();
    if (record) {
      toast.success(isEditing ? "Invoice updated" : "Invoice created");
      onSaved();
    }
  };

  const handleSaveAndDownload = async () => {
    const record = await persist();
    if (!record) return;
    toast.success(isEditing ? "Invoice updated" : "Invoice created");
    try {
      await generateInvoicePdf(record);
    } catch (e) {
      toast.error(e.message || "Invoice saved, but the PDF could not be generated");
    }
    onSaved();
  };

  if (loading || loadingRecord) {
    return <p className="text-slate-400 p-6">Loading invoice…</p>;
  }

  return (
    <div>
      <button onClick={onCancel} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-800 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to invoices
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-800">
            {isEditing ? "Edit Invoice" : "New Invoice"}
          </h2>
          <p className="text-slate-500 text-sm">Invoice # {invoiceNumber}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="h-11 px-5 rounded-xl text-sm font-bold border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            onClick={handleSaveAndDownload}
            disabled={saving}
            className="h-11 px-5 rounded-xl text-sm font-bold inline-flex items-center gap-2 text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-60"
          >
            <Download className="w-4 h-4" /> {saving ? "Working…" : "Save & Download PDF"}
          </button>
        </div>
      </div>

      {/* ── Form controls ── */}
      <div className="rounded-2xl p-5 bg-white shadow-sm border border-slate-100 mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Field label="Client (existing tenant)">
          <select className={inputCls} value={tenantId} onChange={onSelectTenant}>
            <option value="">, Custom / not in system,</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Status">
          <select className={inputCls} value={status} onChange={(e) => setStatus(e.target.value)}
            style={{ color: STATUS_COLORS[status], fontWeight: 700 }}>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Invoice Date">
          <input type="date" className={inputCls} value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
        </Field>
        <Field label="Payment Terms">
          <select className={inputCls} value={paymentTermsDays} onChange={(e) => setPaymentTermsDays(Number(e.target.value))}>
            <option value={7}>Net 7 Days</option>
            <option value={14}>Net 14 Days</option>
            <option value={30}>Net 30 Days</option>
            <option value={45}>Net 45 Days</option>
          </select>
        </Field>
        <Field label="Service Period Month">
          <select className={inputCls} value={periodMonth} onChange={(e) => setPeriodMonth(Number(e.target.value))}>
            {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
          </select>
        </Field>
        <Field label="Service Period Year">
          <input type="number" className={inputCls} value={periodYear} onChange={(e) => setPeriodYear(Number(e.target.value))} />
        </Field>
      </div>

      {/* ── Live invoice preview ── */}
      <InvoicePreview
        invoiceNumber={invoiceNumber}
        status={status}
        invoiceDate={invoiceDate}
        dueDate={dueDate}
        paymentTermsDays={paymentTermsDays}
        servicePeriodText={servicePeriodText}
        client={client}
        setClient={setClient}
        rows={rows}
        updateRow={updateRow}
        addRow={addRow}
        removeRow={removeRow}
        grandTotal={grandTotal}
      />
    </div>
  );
}

// ── The printable-looking invoice sheet, editable in place ──
function InvoicePreview({
  invoiceNumber, status, invoiceDate, dueDate, paymentTermsDays, servicePeriodText,
  client, setClient, rows, updateRow, addRow, removeRow, grandTotal,
}) {
  return (
    <div className="wi-inv-root rounded-2xl border border-slate-100 shadow-sm bg-white overflow-hidden">
      <style>{`
        .wi-inv-root { font-family: 'Segoe UI', Arial, sans-serif; color: #22303F; }
        .wi-inv-sheet { padding: 28px 32px; }
        .wi-inv-top-row { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 4px; }
        .wi-inv-logo-row { display: flex; align-items: flex-start; gap: 12px; }
        .wi-inv-logo-img { width: 50px; height: 50px; object-fit: contain; display: block; }
        .wi-inv-company-name { font-size: 24px; font-weight: 800; color: #1B2733; margin: 0; line-height: 1; }
        .wi-inv-company-tagline { color: #1E6FA8; font-size: 13px; font-weight: 600; margin: 3px 0 0 0; }
        .wi-inv-company-meta { color: #6B7785; font-size: 12px; margin: 2px 0 0 0; }
        .wi-inv-title { font-size: 30px; font-weight: 800; color: #1E6FA8; text-align: right; margin: 0; letter-spacing: 2px; }
        .wi-inv-number { text-align: right; color: #6B7785; font-size: 13px; margin-top: 4px; }
        .wi-inv-status-badge { display: inline-block; margin-top: 8px; padding: 4px 14px; border-radius: 3px; font-size: 11px; font-weight: 700; color: #fff; letter-spacing: 0.5px; }
        .wi-inv-hr { border: none; border-top: 2px solid #1E6FA8; margin: 18px 0 22px 0; }
        .wi-inv-two-col { display: grid; grid-template-columns: 49.5% 49.5%; justify-content: space-between; gap: 8px; margin-bottom: 18px; }
        .wi-inv-two-col > div { display: flex; flex-direction: column; }
        .wi-inv-box-header { background: #1E6FA8; color: #fff; font-size: 11px; font-weight: 700; padding: 8px 12px; letter-spacing: 0.8px; }
        .wi-inv-box-body { flex: 1; display: flex; flex-direction: column; border: 1px solid #DAE3EC; border-top: none; }
        .wi-inv-box-row { display: flex; align-items: center; flex: 1; min-height: 38px; font-size: 13px; border-bottom: 1px solid #DAE3EC; }
        .wi-inv-box-row:last-child { border-bottom: none; }
        .wi-inv-box-row:nth-child(odd) { background: #EEF4FA; }
        .wi-inv-box-label { width: 29%; padding: 8px 10px; color: #6B7785; font-weight: 600; display: flex; align-items: center; flex-shrink: 0; }
        .wi-inv-box-value { flex: 1; min-width: 0; padding: 8px 10px; display: flex; align-items: center; font-weight: 600; color: #1B2733; }
        .wi-inv-box-value input { width: 100%; border: none; background: transparent; font: inherit; font-weight: 600; color: #1B2733; padding: 0; }
        .wi-inv-box-value input:focus { outline: 1px dashed #1E6FA8; border-radius: 2px; }
        .wi-inv-service-section-header { background: #1E6FA8; color: #fff; font-size: 11px; font-weight: 700; padding: 8px 12px; letter-spacing: 0.8px; }
        table.wi-inv-table { width: 100%; border-collapse: collapse; }
        table.wi-inv-table thead th { background: #12233A; color: #fff; font-size: 11px; font-weight: 700; text-align: left; padding: 10px; letter-spacing: 0.5px; }
        table.wi-inv-table thead th.num { text-align: right; }
        table.wi-inv-table tbody td { border-bottom: 1px solid #DAE3EC; border-left: 1px solid #DAE3EC; border-right: 1px solid #DAE3EC; padding: 12px 10px; font-size: 13px; vertical-align: top; }
        table.wi-inv-table tbody td.num { text-align: right; white-space: nowrap; }
        table.wi-inv-table tbody tr:nth-child(even) td { background: #FAFCFE; }
        .wi-inv-sr-cell { color: #1E6FA8; font-weight: 700; width: 34px; }
        .wi-inv-service-select { display: block; width: 100%; font: inherit; font-weight: 700; color: #1B2733; border: none; background: transparent; margin-bottom: 6px; cursor: pointer; }
        .wi-inv-service-subtitle { display: block; color: #6B7785; font-size: 12px; margin-top: 2px; }
        .wi-inv-desc-title { font-weight: 700; color: #1B2733; margin-bottom: 4px; font-size: 13px; }
        .wi-inv-desc-bullets { margin: 0 0 6px 0; padding-left: 14px; list-style: disc; }
        .wi-inv-desc-bullets li { color: #6B7785; font-size: 12px; line-height: 1.5; margin-bottom: 3px; }
        .wi-inv-desc-meta { color: #6B7785; font-size: 12px; line-height: 1.6; }
        .wi-inv-period-editor { display: inline-flex; gap: 4px; align-items: center; margin-left: 4px; }
        .wi-inv-period-editor select, .wi-inv-period-editor input { font: inherit; font-size: 12px; border: 1px solid #DAE3EC; border-radius: 3px; padding: 1px 4px; color: #6B7785; background: #fff; }
        .wi-inv-num-input { width: 70px; text-align: right; font: inherit; font-size: 13px; font-weight: 600; border: none; background: transparent; color: #1B2733; }
        .wi-inv-num-input:focus { outline: 1px dashed #1E6FA8; }
        .wi-inv-rate-cell { display: inline-flex; align-items: center; justify-content: flex-end; gap: 3px; width: 100%; }
        .wi-inv-remove-row-btn { border: none; background: transparent; color: #B23B3B; cursor: pointer; font-size: 11px; font-weight: 700; margin-top: 6px; padding: 0; text-decoration: underline; }
        .wi-inv-add-row-row { text-align: center; padding: 12px 0 18px 0; border-left: 1px solid #DAE3EC; border-right: 1px solid #DAE3EC; border-bottom: 1px solid #DAE3EC; }
        .wi-inv-grand-total-bar { background: #1E6FA8; color: #fff; display: flex; justify-content: space-between; align-items: center; padding: 14px 16px; font-size: 14px; font-weight: 700; }
        .wi-inv-grand-total-bar .amount { font-size: 17px; font-weight: 800; letter-spacing: 0.5px; }
        .wi-inv-footer-divider { border: none; border-top: 1px solid #DAE3EC; margin: 32px 0 12px 0; }
        .wi-inv-footer-text { text-align: center; color: #6B7785; font-size: 11px; margin: 0; padding-bottom: 4px; }
        .wi-inv-btn { font-family: inherit; font-size: 13px; font-weight: 600; padding: 8px 16px; border-radius: 6px; border: 1px solid #1E6FA8; background: #fff; color: #1E6FA8; cursor: pointer; }
        .wi-inv-btn:hover { background: #EEF4FA; }
      `}</style>

      <div className="wi-inv-sheet">
        <div className="wi-inv-top-row">
          <div className="wi-inv-logo-row">
            <img className="wi-inv-logo-img" src={COMPANY_LOGO} alt="Webideal logo" />
            <div>
              <p className="wi-inv-company-name">{COMPANY.name}</p>
              <p className="wi-inv-company-tagline">{COMPANY.tagline}</p>
              <p className="wi-inv-company-meta">{COMPANY.address}</p>
              <p className="wi-inv-company-meta">{COMPANY.phone} | {COMPANY.website}</p>
            </div>
          </div>
          <div>
            <p className="wi-inv-title">INVOICE</p>
            <p className="wi-inv-number"># {invoiceNumber}</p>
            <div style={{ textAlign: "right" }}>
              <span className="wi-inv-status-badge" style={{ background: STATUS_COLORS[status] }}>{status}</span>
            </div>
          </div>
        </div>

        <hr className="wi-inv-hr" />

        <div className="wi-inv-two-col">
          <div>
            <div className="wi-inv-box-header">INVOICE DETAILS</div>
            <div className="wi-inv-box-body">
              <div className="wi-inv-box-row">
                <div className="wi-inv-box-label">Invoice No.</div>
                <div className="wi-inv-box-value">{invoiceNumber}</div>
              </div>
              <div className="wi-inv-box-row">
                <div className="wi-inv-box-label">Invoice Date</div>
                <div className="wi-inv-box-value">{formatDateDMY(invoiceDate)}</div>
              </div>
              <div className="wi-inv-box-row">
                <div className="wi-inv-box-label">Due Date</div>
                <div className="wi-inv-box-value">{formatDateDMY(dueDate)}</div>
              </div>
              <div className="wi-inv-box-row">
                <div className="wi-inv-box-label">Payment Terms</div>
                <div className="wi-inv-box-value">Net {paymentTermsDays} Days</div>
              </div>
              <div className="wi-inv-box-row">
                <div className="wi-inv-box-label">Service Period</div>
                <div className="wi-inv-box-value">{servicePeriodText}</div>
              </div>
            </div>
          </div>

          <div>
            <div className="wi-inv-box-header">BILL TO</div>
            <div className="wi-inv-box-body">
              <div className="wi-inv-box-row">
                <div className="wi-inv-box-label">Client</div>
                <div className="wi-inv-box-value">
                  <input value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} placeholder="Client / company name" />
                </div>
              </div>
              <div className="wi-inv-box-row">
                <div className="wi-inv-box-label">Address</div>
                <div className="wi-inv-box-value">
                  <input value={client.address} onChange={(e) => setClient({ ...client, address: e.target.value })} placeholder="Billing address" />
                </div>
              </div>
              <div className="wi-inv-box-row">
                <div className="wi-inv-box-label">Contact</div>
                <div className="wi-inv-box-value">
                  <input value={client.contact} onChange={(e) => setClient({ ...client, contact: e.target.value })} placeholder="Contact person" />
                </div>
              </div>
              <div className="wi-inv-box-row">
                <div className="wi-inv-box-label">Email</div>
                <div className="wi-inv-box-value">
                  <input value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} placeholder="billing@client.com" />
                </div>
              </div>
              <div className="wi-inv-box-row">
                <div className="wi-inv-box-label">Phone</div>
                <div className="wi-inv-box-value">
                  <input value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} placeholder="+91 …" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="wi-inv-service-section-header">SERVICE DETAILS</div>
        <table className="wi-inv-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>SR</th>
              <th style={{ width: 135 }}>SERVICE</th>
              <th>DESCRIPTION</th>
              <th className="num" style={{ width: 62 }}>USERS</th>
              <th className="num" style={{ width: 110 }}>RATE / USER</th>
              <th className="num" style={{ width: 115 }}>TOTAL (Rs.)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const preset = SERVICE_PRESETS[row.serviceKey];
              const days = daysInMonth(row.month, row.year);
              const total = (Number(row.users) || 0) * (Number(row.rate) || 0);
              return (
                <tr key={row.id}>
                  <td className="wi-inv-sr-cell">{pad2(idx + 1)}</td>
                  <td>
                    <select
                      className="wi-inv-service-select"
                      value={row.serviceKey}
                      onChange={(e) => updateRow(row.id, { serviceKey: e.target.value })}
                    >
                      {SERVICE_KEYS.map((k) => (
                        <option key={k} value={k}>{SERVICE_PRESETS[k].service}</option>
                      ))}
                    </select>
                    <div className="wi-inv-service-subtitle">{preset.subtitle}</div>
                  </td>
                  <td>
                    <div className="wi-inv-desc-title">{preset.title}</div>
                    <ul className="wi-inv-desc-bullets">
                      {preset.bullets.join(" • ").split(" • ").map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                    <div className="wi-inv-desc-meta">Billing Cycle: Monthly</div>
                    <div className="wi-inv-desc-meta">Plan: {preset.plan}</div>
                    <div className="wi-inv-desc-meta">
                      Period: {days} Days (
                      <span className="wi-inv-period-editor">
                        <select value={row.month} onChange={(e) => updateRow(row.id, { month: Number(e.target.value) })}>
                          {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
                        </select>
                        <input type="number" style={{ width: 56 }} value={row.year} onChange={(e) => updateRow(row.id, { year: Number(e.target.value) })} />
                      </span>
                      )
                    </div>
                    {rows.length > 1 && (
                      <button className="wi-inv-remove-row-btn" onClick={() => removeRow(row.id)}>✕ Remove row</button>
                    )}
                  </td>
                  <td className="num">
                    <input
                      type="number"
                      className="wi-inv-num-input"
                      min={0}
                      value={row.users}
                      onChange={(e) => updateRow(row.id, { users: e.target.value })}
                    />
                  </td>
                  <td className="num">
                    <span className="wi-inv-rate-cell">
                      <span>Rs.</span>
                      <input
                        type="number"
                        className="wi-inv-num-input"
                        style={{ width: 56 }}
                        min={0}
                        value={row.rate}
                        onChange={(e) => updateRow(row.id, { rate: e.target.value })}
                      />
                    </span>
                  </td>
                  <td className="num" style={{ fontWeight: 700 }}>Rs. {formatINR(total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="wi-inv-add-row-row">
          <button className="wi-inv-btn" onClick={addRow}>+ Add another service</button>
        </div>

        <div className="wi-inv-grand-total-bar">
          <span>GRAND TOTAL</span>
          <span className="amount">Rs. {formatINR(grandTotal)}</span>
        </div>

        <hr className="wi-inv-footer-divider" />
        <p className="wi-inv-footer-text">
          {COMPANY.name} &nbsp;|&nbsp; {COMPANY.address} &nbsp;|&nbsp; {COMPANY.website}
        </p>
      </div>
    </div>
  );
}